"use client"
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useCannedResponses } from "@/components/chat/hooks/useCannedResponses";
import { useChatSubscriptions } from "@/components/chat/hooks/useChatSubscriptions";
import { Message } from "@/components/chat/types";
import { useChatInitialization } from "@/components/chat/hooks/useChatInitialization";
interface ChatContextType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chats: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setChats: React.Dispatch<React.SetStateAction<any[]>>;
  activeChatIndex: number;
  setActiveChatIndex: (index: number) => void;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cannedResponses: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customResponses: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCustomResponses: React.Dispatch<React.SetStateAction<any[]>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customCategories: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCustomCategories: React.Dispatch<React.SetStateAction<any[]>>;
  showCannedResponsesSheet: boolean;
  setShowCannedResponsesSheet: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  refreshChats: () => Promise<void>;
  findChatIndexById: (chatId: string) => number;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [activeChatIndex, setActiveChatIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastChangedChatId, setLastChangedChatId] = useState<string | null>(
    null
  );
  const previousActiveChatIndexRef = useRef<number>(-1);

  const {
    chats,
    setChats,
    isLoading,
    initializeChats,
    refreshChats,
    isMounted,
    isRefreshingRef,
    manualRefresh,
    lastSuccessfulFetchRef,
    initializeAttemptedRef,
    setupRealtimeSubscription,
  } = useChatInitialization();

  const {
    cannedResponses,
    customResponses,
    setCustomResponses,
    customCategories,
    setCustomCategories,
    showCannedResponsesSheet,
    setShowCannedResponsesSheet,
  } = useCannedResponses();

  const { cleanupAllSubscriptions } = useChatSubscriptions();
  useEffect(() => {
      console.log("chats changed", chats)
  }, [chats])

  useEffect(() => {
    initializeChats();
  }, [initializeChats]);

  const setActiveChatIndexWithValidation = (index: number) => {
    if (previousActiveChatIndexRef.current === index) {
      return;
    }

    if (index < 0) {
      return;
    }

    if (chats.length > 0 && index >= chats.length) {
      return;
    }

    previousActiveChatIndexRef.current = index;

    setActiveChatIndex(index);

    if (chats.length > 0 && index >= 0 && index < chats.length) {
      const selectedChat = chats[index];
      setLastChangedChatId(selectedChat.id);
    }
  };

  const findChatIndexById = (chatId: string) => {
    for (let i = 0; i < chats.length; i++) {
      if (chats[i].id === chatId) {
        return i;
      }
    }
    return -1;
  };

  useEffect(() => {
    if (chats.length > 0 && lastChangedChatId) {
      const chatIndex = findChatIndexById(lastChangedChatId);

      if (chatIndex !== -1) {
        if (chatIndex !== activeChatIndex) {
          previousActiveChatIndexRef.current = chatIndex;
          setActiveChatIndex(chatIndex);
        }
      }
    }
  }, [chats, lastChangedChatId, activeChatIndex]);

  // useEffect(() => {
  //   const intervalId = setInterval(() => {
  //     manualRefresh();
  //   }, 5000);
  //   return () => clearInterval(intervalId);
  // }, []);

  useEffect(() => {
    const cleanupRealtimeSub = setupRealtimeSubscription(
      () => {
        if (isMounted.current && !isRefreshingRef.current) {
          initializeChats();
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (chatId: any, newMessageData: any) => {
        if (!isMounted.current) return;

        setChats((currentChats) => {
          const chatIndex = currentChats.findIndex(
            (chat) => chat.id === chatId
          );

          if (chatIndex !== -1) {
            const newMessage: Message = {
              id: newMessageData.id,
              text: newMessageData.text,
              sender: newMessageData.sender,
              timestamp: new Date(newMessageData.timestamp).getTime(),
              isRead: newMessageData.is_read,
            };

            const updatedChats = [...currentChats];

            updatedChats[chatIndex] = {
              ...updatedChats[chatIndex],
              messages: [...updatedChats[chatIndex].messages, newMessage],
              lastActivity: Date.now(),
            };

            return updatedChats;
          }

          return currentChats;
        });
      },
      isMounted,
      isRefreshingRef,
      chats
    );

    if (!initializeAttemptedRef.current) {
      initializeChats();
    }

    return () => {
      isMounted.current = false;

      cleanupAllSubscriptions();
      cleanupRealtimeSub();
    };
  }, [
    chats,
    setupRealtimeSubscription,
    initializeChats,
    cleanupAllSubscriptions,
    isMounted,
    isRefreshingRef,
    lastSuccessfulFetchRef,
    initializeAttemptedRef,
  ]);

  const value = {
    chats,
    setChats,
    activeChatIndex,
    setActiveChatIndex: setActiveChatIndexWithValidation,
    isSubmitting,
    setIsSubmitting,
    cannedResponses,
    customResponses,
    setCustomResponses,
    customCategories,
    setCustomCategories,
    showCannedResponsesSheet,
    setShowCannedResponsesSheet,
    isLoading,
    refreshChats,
    findChatIndexById,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChatState() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
}
